import React, { useEffect, useState } from "react"
import {
	Button,
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
	Input,
	ModalBody,
	ModalFooter,
	ModalHeader,
	UncontrolledButtonDropdown,
} from "reactstrap"
import { getSportsConfig } from "../../../utility/constants/sportConfig"
import styles from "../EventsTable.module.scss"
import { getAllEvents, getEventMessage } from "../../../utility/Utils"
import ConfirmEditModal from "./ConfirmEditModal"
import { EditTimeComponent } from "../../"

function ModalContent({ dismissModal, initialEvent }) {
	// Defining events for this sport
	const allEvents = getAllEvents()
	const { doesntHaveTime } = getSportsConfig(localStorage.getItem("sport"))

	// Defining our state, we have: event, team, player, followup player/event, comment, quarter, time
	// * State
	const [comment, setComment] = useState(initialEvent.comment)

	const [selectedEvent, setSelectedEvent] = useState(
		getEvent(initialEvent.event, allEvents)
	)
	const [selectedTeam, setSelectedTeam] = useState(initialEvent.team)
	const [selectedPlayer, setSelectedPlayer] = useState(
		getPlayer(initialEvent.playerId)
	)

	const [followupPlayer, setFollowupPlayer] = useState(
		getPlayer(initialEvent.player2Id)
	)
	const [followupEvent, setFollowupEvent] = useState(initialEvent.followupEvent)
	// The following state is for the EditTimeComponent
	const [time, setTime] = useState(initialEvent.time)
	const [quarter, setQuarter] = useState("")
	const [hasError, setHasError] = useState(false)

	useEffect(() => {
		updateComment()
	}, [selectedEvent, selectedTeam, selectedPlayer, followupPlayer, followupEvent])

	// Defining teams for this match
	const teams = getTeams(selectedTeam ?? initialEvent.team)

	// * Modifiers
	const updateComment = () => {
		if (
			selectedEvent?.name === initialEvent.event &&
			selectedEvent?.noPlayer &&
			(selectedEvent?.noTeam || selectedTeam === initialEvent.team)
		) {
			return setComment(initialEvent.comment)
		}

		if (selectedEvent) {
			const newComment = getEventMessage(
				selectedEvent,
				{ ...selectedPlayer, team: selectedTeam },
				followupPlayer
					? {
							...followupPlayer,
							team: teams.teamPlayers.players.find(
								(player) =>
									player?.playerId === followupPlayer?.playerId
							)
								? teams.teamPlayers.name
								: teams.enemyPlayers.name,
					  }
					: {},
				followupEvent
			)
			setComment(newComment)
		}
	}

	const onSetPlayer = (value) => {
		if (value.playerId === selectedPlayer?.playerId) return
		if (value.playerId === followupPlayer?.playerId) {
			setFollowupPlayer(selectedPlayer)
		}
		setSelectedPlayer(value)
	}

	const onSetFollowupPlayer = (value) => {
		if (value.playerId === followupPlayer?.playerId) return
		if (value.playerId === selectedPlayer?.playerId) {
			setSelectedPlayer(followupPlayer)
		}
		setFollowupPlayer(value)
	}

	const onSetTeam = (value) => {
		if (value === selectedTeam) return
		setSelectedTeam(value)
		setSelectedPlayer({})
		setFollowupPlayer({})
	}

	const onSetEvent = (value) => {
		if (value?.followup?.type !== selectedEvent?.followup?.type) {
			setFollowupPlayer({})
			setFollowupEvent({})
		}

		setSelectedEvent(allEvents.find((event) => event.name === value.name))
	}

	// * UI Builders
	const createDropdown = (
		placeHolder,
		selectedText,
		options,
		setter,
		getText = (elem) => elem?.name
	) => {
		return (
			<UncontrolledButtonDropdown size="md">
				<DropdownToggle color="primary" caret outline={!!selectedText}>
					{selectedText ?? placeHolder}
				</DropdownToggle>
				<DropdownMenu>
					{options.map((option, index) => (
						<DropdownItem
							key={`option-${placeHolder}-${index}`}
							onClick={() => setter(option)}
						>
							{getText(option)}
						</DropdownItem>
					))}
				</DropdownMenu>
			</UncontrolledButtonDropdown>
		)
	}

	const createTeam = () => {
		if (selectedEvent?.noTeam) return <> </>
		const teams = [
			JSON.parse(localStorage.getItem("homeTeam")).name,
			JSON.parse(localStorage.getItem("awayTeam")).name,
		]

		return (
			<>
				<p>Ekipa</p>
				{createDropdown(
					"Izaberi Ekipu",
					selectedTeam,
					teams,
					onSetTeam,
					(elem) => elem
				)}
			</>
		)
	}

	const createPlayer = () => {
		if (selectedEvent?.noPlayer) return <> </>
		return (
			<>
				<p>Igrač</p>
				{createDropdown(
					"Izaberi igrača",
					selectedPlayer?.name &&
						`(${selectedPlayer.tshirtNumber}) ${selectedPlayer.name} ${selectedPlayer.surname}`,
					teams["teamPlayers"].players,
					onSetPlayer,
					(elem) => `(${elem.tshirtNumber}) ${elem.name} ${elem.surname}`
				)}
			</>
		)
	}

	const createFollowup = () => {
		const { followup } = selectedEvent ?? {}
		if (!followup) return <></>
		if (followup.type === "event") {
			return (
				<>
					<p>Followup Event</p>
					{createDropdown(
						"Izaberi Followup",
						followupEvent && typeof followupEvent !== "object"
							? followupEvent
							: "-",
						selectedEvent.followup.events,
						setFollowupEvent,
						(elem) => elem
					)}
				</>
			)
		}
		if (followup.type === "enemyPlayers" || followup.type === "teamPlayers") {
			return (
				<>
					<p>Followup Igrač</p>
					{createDropdown(
						"Izaberi Followup",
						followupPlayer?.name &&
							`(${followupPlayer.tshirtNumber}) ${followupPlayer.name} ${followupPlayer.surname}`,
						teams[followup.type].players,
						onSetFollowupPlayer,
						(elem) =>
							`(${elem.tshirtNumber}) ${elem.name} ${elem.surname}`
					)}
				</>
			)
		}
		return <></>
	}

	return (
		<>
			<ModalHeader toggle={dismissModal}>Edit event</ModalHeader>
			<ModalBody className={styles.modalBody}>
				<p>Tip eventa</p>
				{createDropdown(
					"Izaberi event",
					selectedEvent?.name,
					allEvents,
					onSetEvent
				)}
				{createTeam()}
				{createPlayer()}
				{createFollowup()}
				{!doesntHaveTime && (
					<>
						<hr />
						<EditTimeComponent
							initialTime={initialEvent.time}
							initialQuarter={initialEvent.quarter}
							time={time}
							setTime={setTime}
							quarter={quarter}
							setQuarter={setQuarter}
							setHasError={setHasError}
						/>
					</>
				)}
				<div className={styles.comment}>
					<hr />
					<p>Komentar</p>
					<cite>
						Nemoj unijeti igrače koji nisu izabrani u opcijama iznad.
					</cite>
					<Input
						type="textarea"
						placeholder="Unesi komentar"
						value={comment}
						onChange={({ target }) => {
							setComment(target.value)
						}}
					/>
				</div>
			</ModalBody>
			<ModalFooter>
				{!hasError && (
					<ConfirmEditModal
						toSend={{
							selectedEvent,
							selectedTeam,
							selectedPlayer,
							followupPlayer,
							followupEvent,
							comment,
							eventId: initialEvent.id,
							time: time || initialEvent.time,
							quarter: quarter || initialEvent.quarter,
							order: initialEvent.order,
						}}
						dismissModal={dismissModal}
						initialTeam={initialEvent.team}
						initialComment={initialEvent.comment}
						initialEvent={getEvent(initialEvent.event, allEvents)}
					/>
				)}
				<Button.Ripple color="secondary" outline onClick={dismissModal}>
					Odustani
				</Button.Ripple>
			</ModalFooter>
		</>
	)
}

export default ModalContent

// * Helper functions
const getTeams = (teamName) => {
	const homeTeam = JSON.parse(localStorage.getItem("homeTeam"))
	const awayTeam = JSON.parse(localStorage.getItem("awayTeam"))
	if (homeTeam.name === teamName) {
		return {
			teamPlayers: {
				name: homeTeam.name,
				players: homeTeam.players,
			},
			enemyPlayers: {
				name: awayTeam.name,
				players: awayTeam.players,
			},
		}
	}
	return {
		teamPlayers: {
			name: awayTeam.name,
			players: awayTeam.players,
		},
		enemyPlayers: {
			name: homeTeam.name,
			players: homeTeam.players,
		},
	}
}

const getPlayer = (playerId) => {
	let players = [
		...JSON.parse(localStorage.getItem("awayTeam")).players,
		...JSON.parse(localStorage.getItem("homeTeam")).players,
	]

	return players.find((player) => player.playerId === playerId)
}

const getEvent = (initialEvent, allEvents) => {
	if (!initialEvent) return {}
	if (initialEvent === "comment")
		return { name: "Komentar", noPlayer: true, noTeam: true }
	if (initialEvent === "team comment")
		return { name: "Ekipni Komentar", noPlayer: true }

	const toReturn = allEvents.find((event) => event.name === initialEvent)
	return toReturn
}
